// Staff, station PINs and roles: API calls and TanStack Query options (B-080). Every
// response is parsed with the shared @enp/domain/api schema (lib/api.ts). Creates send one
// idempotency key per opened form, so a double click or a retry makes one person or role.

import {
  type RoleCreateRequest,
  type RoleUpdateRequest,
  type StaffCreateRequest,
  type StaffRoleAssignment,
  type StaffUpdateRequest,
  pinUnlockResponseSchema,
  roleSchema,
  rolesResponseSchema,
  staffListResponseSchema,
  staffMemberSchema,
} from '@enp/domain/api';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';

export type StaffFilter = 'active' | 'archived';

/** The staff list's filter in the URL. */
export const staffSearchSchema = z.object({
  status: z.enum(['active', 'archived']).optional().catch(undefined),
});

export const staffKeys = {
  all: ['staff'] as const,
  list: (status: StaffFilter) => ['staff', 'list', status] as const,
  roles: () => ['staff', 'roles'] as const,
};

export const staffQuery = (status: StaffFilter = 'active') =>
  queryOptions({
    queryKey: staffKeys.list(status),
    queryFn: ({ signal }) =>
      api(`/staff?status=${status}`, { signal, schema: staffListResponseSchema }),
    retry: false,
  });

export const rolesQuery = () =>
  queryOptions({
    queryKey: staffKeys.roles(),
    queryFn: ({ signal }) => api('/roles', { signal, schema: rolesResponseSchema }),
    retry: false,
  });

export const createStaff = (body: StaffCreateRequest, idempotencyKey: string) =>
  api('/staff', {
    method: 'POST',
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    schema: staffMemberSchema,
  });

export const updateStaff = (id: string, body: StaffUpdateRequest) =>
  api(`/staff/${encodeURIComponent(id)}`, { method: 'PATCH', body, schema: staffMemberSchema });

export const setStaffRoles = (id: string, roles: StaffRoleAssignment[]) =>
  api(`/staff/${encodeURIComponent(id)}/roles`, {
    method: 'PUT',
    body: { roles },
    schema: staffMemberSchema,
  });

export const setStaffPin = (id: string, pin: string) =>
  api(`/staff/${encodeURIComponent(id)}/pin`, {
    method: 'POST',
    body: { pin },
    schema: staffMemberSchema,
  });

export const unlockPinPad = (deviceId: string) =>
  api(`/devices/${encodeURIComponent(deviceId)}/unlock-pin`, {
    method: 'POST',
    schema: pinUnlockResponseSchema,
  });

export const createRole = (body: RoleCreateRequest, idempotencyKey: string) =>
  api('/roles', {
    method: 'POST',
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    schema: roleSchema,
  });

export const updateRole = (id: string, body: RoleUpdateRequest) =>
  api(`/roles/${encodeURIComponent(id)}`, { method: 'PATCH', body, schema: roleSchema });

/** After any change: people, roles and their counts are read again. */
export const invalidateStaff = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: staffKeys.all });
